<script setup lang="ts">
const props = defineProps<{
  path: string;
  mkdirOnUpload?: boolean;
  // Server-side ACLs remain authoritative; used for candidate self-service uploads.
  skipPermissionCheck?: boolean;
}>();

const { t } = useI18n({ useScope: "local" });

const route = useRoute();
const { site } = useSite();
const { profile } = useProfile();
const { hasPermission, hasContentPermission, isInternalContentType } =
  usePermissions();
const siteId = computed(() => {
  const routeSiteId = route.params.siteId;
  if (Array.isArray(routeSiteId)) {
    return routeSiteId[0] || site.value?.id || "";
  }
  return (routeSiteId as string) || site.value?.id || "";
});

interface SiteFilesInjection {
  files: any;
  folderPath: string;
}

const siteFiles = inject<SiteFilesInjection | null>("siteFiles", null);

const { create, get, uploading } = useFileEntries(siteId);
const shouldMkdirOnUpload = computed(() => props.mkdirOnUpload ?? false);
const profilePermissions = computed(() => profile.value?.permissions ?? []);
const canUpload = computed(() =>
  props.skipPermissionCheck ||
  hasPermission(profilePermissions.value, "files:write")
);

function canUploadFile(file: File) {
  if (props.skipPermissionCheck) {
    return true;
  }

  if (!canUpload.value) {
    return false;
  }

  if (
    isInternalContentType(file.type) &&
    !hasPermission(profilePermissions.value, "internal:admin")
  ) {
    return false;
  }

  return hasContentPermission(profilePermissions.value, file.type, "write");
}

/**
 * If a dropped file is a JSON resource definition, update its metadata and
 * return a new File instance with the correct content type.
 */
async function prepareFile(file: File): Promise<File> {
  try {
    const text = await file.text();
    const data = JSON.parse(text);
    if (
      !data?.metadata?.type ||
      !data.metadata.type.startsWith(
        "application/vnd.pivotalgroup.resource-definition"
      )
    ) {
      return file;
    }

    // Ensure id and siteId are set correctly
    data.metadata.id = crypto.randomUUID();
    data.metadata.siteId = siteId.value;
    data.metadata.name = file.name;

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: data.metadata.type });
    return new File([blob], file.name, { type: data.metadata.type });
  } catch (err) {
    console.error("Failed to parse dropped JSON", err);
    return file;
  }
}

const dragActive = ref(false);
const currentFile = ref<string | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);

function normalizePath(path: string) {
  return path.replace(/\\/g, "/").replace(/\/+/g, "/").replace(/\/$/, "");
}

function toFileRecords(files: FileList | File[], basePath: string) {
  const normalizedBasePath = normalizePath(basePath);
  const records: { parentPath: string; file: File }[] = [];
  const seen = new Set<string>();

  for (const file of Array.from(files)) {
    const relativePath = (file as File & { webkitRelativePath?: string })
      .webkitRelativePath;
    const relativeParent =
      relativePath && relativePath.includes("/")
        ? relativePath.split("/").slice(0, -1).join("/")
        : "";
    const parentPath = normalizePath(
      relativeParent
        ? `${normalizedBasePath}/${relativeParent}`
        : normalizedBasePath
    );

    const dedupeKey = `${parentPath}|${file.name}|${file.size}|${file.lastModified}`;
    if (seen.has(dedupeKey)) {
      continue;
    }
    seen.add(dedupeKey);

    records.push({ parentPath, file });
  }

  return records;
}

async function uploadFileRecords(fileRecords: { parentPath: string; file: File }[]) {
  if (fileRecords.length === 0) return;

  // Upload files directly. When mkdir is enabled, backend creates missing folders.
  for (const { parentPath, file } of fileRecords) {
    const fullParent = parentPath.replace(/\/$/, "");
    currentFile.value = t("uploadingFile", {
      file: file.name,
      path: fullParent,
    });
    const prepared = await prepareFile(file);
    if (!canUploadFile(prepared)) {
      console.warn("Skipping upload; missing write permission for file type", {
        type: prepared.type,
      });
      currentFile.value = null;
      continue;
    }

    await create(fullParent, prepared, { mkdir: shouldMkdirOnUpload.value });

    currentFile.value = null;
  }

  if (!siteFiles) {
    return;
  }

  const refreshedFiles = await get(props.path, "flags,metadata");
  if (refreshedFiles) {
    siteFiles.files = refreshedFiles;
    siteFiles.folderPath = props.path;
  }
}

function openFilePicker() {
  if (uploading.value || !canUpload.value) return;
  fileInput.value?.click();
}

async function onFileSelection(event: Event) {
  const input = event.target as HTMLInputElement;
  const files = input.files;
  if (!files || files.length === 0) {
    return;
  }

  const fileRecords = toFileRecords(files, props.path);

  await uploadFileRecords(fileRecords);

  // Reset value so selecting the same file again still triggers change.
  input.value = "";
}

/**
 * onDrop: Step 1 = collect all file entries into `fileRecords` (parentPath + File).
 *         Step 2 = once collection is complete, create folders & upload files in sequence.
 */
async function onDrop(event: DragEvent) {
  event.preventDefault();
  dragActive.value = false;

  if (!canUpload.value) {
    return;
  }

  const files = event.dataTransfer?.files;
  if (!files || files.length === 0) {
    return;
  }

  const fileRecords = toFileRecords(files, props.path);
  await uploadFileRecords(fileRecords);
}
</script>

<template>
  <div
    v-if="!uploading && canUpload"
    class="group relative block w-full cursor-pointer rounded-lg border-muted-foreground border-2 border-dashed p-12 text-center hover:border-primary"
    role="button"
    tabindex="0"
    @dragover.prevent="dragActive = true"
    @dragenter.prevent="dragActive = true"
    @dragleave.prevent="dragActive = false"
    @drop.prevent="onDrop"
    @click="openFilePicker"
    @keydown.enter.prevent="openFilePicker"
    @keydown.space.prevent="openFilePicker"
    :class="{ 'border-primary bg-gray-50': dragActive }"
  >
    <input
      ref="fileInput"
      type="file"
      class="hidden"
      multiple
      @change="onFileSelection"
    />
    <icon mode="svg" name="heroicons:arrow-up-tray" class="mx-auto block text-4xl" />
    <span class="mt-2 block text-sm font-semibold">{{ t("dropFiles") }}</span>
    <span class="mt-2 block text-sm text-muted-foreground">
      {{ t("dropHint") }}
    </span>
  </div>
  <div
    v-else-if="uploading"
    class="group relative block w-full rounded-lg border-muted border-2 border-dashed p-12 text-center"
  >
    <icon mode="svg"
      name="lucide:loader-circle"
      class="mx-auto text-4xl text-primary animate-spin"
    />
    <span class="mt-2 block text-sm font-semibold">{{
      t("uploadingFiles")
    }}</span>
    <span class="mt-2 block text-sm text-muted-foreground">
      {{ currentFile || t("preparingUpload") }}
    </span>
  </div>
</template>

<i18n lang="yaml">
en:
  dropFiles: Drop files to upload
  dropHint: Drag and drop files or folders here to upload them to the current directory.
  uploadingFiles: Uploading files...
  preparingUpload: Preparing to upload files...
  creatingFolder: "Creating folder: {folder}"
  uploadingFile: "Uploading: {file} to {path}"
</i18n>
